import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  FiDollarSign,
  FiTrendingUp,
  FiShoppingBag,
  FiTruck,
  FiMapPin,
  FiClock,
  FiChevronRight,
  FiX,
  FiAlertCircle,
} from "react-icons/fi";

const fmtMoney = (n, sym = "₺") =>
  sym +
  parseFloat(n || 0).toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtNum = (n) => Number(n || 0).toLocaleString("tr-TR");

const Bar = ({ value, max, color = "#378ADD" }) => (
  <div style={{ flex: 1, height: 6, background: "var(--bs-gray-200,#e9ecef)", borderRadius: 3, overflow: "hidden" }}>
    <div style={{ width: `${Math.round((value / Math.max(max, 1)) * 100)}%`, height: "100%", background: color, borderRadius: 3, transition: "width .4s" }} />
  </div>
);

const MetricMini = ({ label, value, color }) => (
  <div style={{ flex: 1, background: "var(--bs-gray-100,#f8f9fa)", borderRadius: 8, padding: "10px 12px" }}>
    <div style={{ fontSize: 11, color: "#6c757d" }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 500, marginTop: 2, color: color || "inherit" }}>{value}</div>
  </div>
);

const NoData = ({ label = "Veri yükleniyor..." }) => (
  <div className="text-center py-4 text-muted" style={{ fontSize: 13 }}>
    <FiAlertCircle style={{ fontSize: 22, display: "block", margin: "0 auto 8px" }} />
    {label}
  </div>
);

export default function SalesAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPanel, setOpenPanel] = useState(null);
  const chartRefMonthly = useRef(null);
  const chartInstanceMonthly = useRef(null);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/customers/sales-analytics`)
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const ensureChart = () =>
    new Promise((res) => {
      if (window.Chart) return res();
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
      s.onload = res;
      document.head.appendChild(s);
    });

  useEffect(() => {
    if (openPanel !== "monthly" || !chartRefMonthly.current || !data) return;
    let alive = true;
    const build = async () => {
      await ensureChart();
      if (!alive || !chartRefMonthly.current) return;
      if (chartInstanceMonthly.current) { chartInstanceMonthly.current.destroy(); chartInstanceMonthly.current = null; }
      const sym = data.currencySymbol || "₺";
      chartInstanceMonthly.current = new window.Chart(chartRefMonthly.current, {
        type: "bar",
        data: {
          labels: ["Günlük", "Haftalık", "Aylık", "Toplam"],
          datasets: [{
            label: "Gelir",
            data: [
              parseFloat(data.dailyRevenue || 0),
              parseFloat(data.weeklyRevenue || 0),
              parseFloat(data.monthlyRevenue || 0),
              parseFloat(data.totalRevenue || 0),
            ],
            backgroundColor: ["#378ADD", "#1D9E75", "#BA7517", "#534AB7"],
            borderRadius: 4,
            borderWidth: 0,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { grid: { display: false }, ticks: { font: { size: 11 } } },
            y: { grid: { color: "rgba(128,128,128,0.1)" }, ticks: { font: { size: 11 }, callback: (v) => sym + v.toLocaleString("tr-TR") } },
          },
        },
      });
    };
    build();
    return () => {
      alive = false;
      if (chartInstanceMonthly.current) { chartInstanceMonthly.current.destroy(); chartInstanceMonthly.current = null; }
    };
  }, [openPanel, data]);

  const sym = data?.currencySymbol || "₺";

  const cards = [
    { id: "totalRevenue", icon: <FiDollarSign />, iconClass: "text-success bg-soft-success border-soft-success", label: "Toplam Gelir", value: loading ? "..." : fmtMoney(data?.totalRevenue, sym), sub: "tamamlanan siparişler" },
    { id: "pending",      icon: <FiClock />,      iconClass: "text-warning bg-soft-warning border-soft-warning", label: "Ödeme Bekleyen", value: loading ? "..." : fmtMoney(data?.pendingRevenue, sym), sub: "pending siparişler" },
    { id: "aov",          icon: <FiShoppingBag />,iconClass: "text-primary bg-soft-primary border-soft-primary", label: "Ort. Sipariş", value: loading ? "..." : fmtMoney(data?.avgOrderValue, sym), sub: "sipariş başına ortalama" },
    { id: "cargo",        icon: <FiTruck />,      iconClass: "text-info bg-soft-info border-soft-info", label: "Ort. Kargo", value: loading ? "..." : fmtMoney(data?.avgCargo, sym), sub: "sipariş başına kargo" },
    { id: "daily",        icon: <FiTrendingUp />, iconClass: "text-success bg-soft-success border-soft-success", label: "Günlük Gelir", value: loading ? "..." : fmtMoney(data?.dailyRevenue, sym), sub: "bugün" },
    { id: "weekly",       icon: <FiTrendingUp />, iconClass: "text-primary bg-soft-primary border-soft-primary", label: "Haftalık Gelir", value: loading ? "..." : fmtMoney(data?.weeklyRevenue, sym), sub: "son 7 gün" },
    { id: "monthly",      icon: <FiTrendingUp />, iconClass: "text-info bg-soft-info border-soft-info", label: "Aylık Gelir", value: loading ? "..." : fmtMoney(data?.monthlyRevenue, sym), sub: "son 30 gün" },
    {
      id: "country",
      icon: <FiMapPin />,
      iconClass: "text-danger bg-soft-danger border-soft-danger",
      label: "En Çok Sipariş",
      value: loading ? "..." : (data?.countryStats?.[0]?.country || "—"),
      sub: data?.countryStats?.[0] ? `${fmtNum(data.countryStats[0].order_count)} sipariş` : "ülke verisi",
    },
  ];

  const renderPanel = (id) => {
    if (!data) return <NoData label="Veri alınamadı." />;

    if (id === "totalRevenue") {
      return (
        <>
          <div className="row g-2 mb-3">
            <div className="col-4"><MetricMini label="Toplam gelir" value={fmtMoney(data.totalRevenue, sym)} color="#1D9E75" /></div>
            <div className="col-4"><MetricMini label="AOV" value={fmtMoney(data.avgOrderValue, sym)} /></div>
            <div className="col-4"><MetricMini label="Bekleyen" value={fmtMoney(data.pendingRevenue, sym)} color="#BA7517" /></div>
          </div>
          <p className="text-muted small mb-0">
            Tamamlanan siparişlerin (confirmed / paid / preparing / shipped / delivered) döviz kuru dönüşümü yapılmış toplam geliridir.
          </p>
        </>
      );
    }

    if (id === "pending") {
      return (
        <div className="row g-2">
          <div className="col-6"><MetricMini label="Bekleyen tutar" value={fmtMoney(data.pendingRevenue, sym)} color="#BA7517" /></div>
          <div className="col-6">
            <MetricMini
              label="Toplam gelirin yüzdesi"
              value={data.totalRevenue > 0 ? `%${((data.pendingRevenue / data.totalRevenue) * 100).toFixed(1)}` : "—"}
            />
          </div>
        </div>
      );
    }

    if (id === "aov") {
      return (
        <>
          <div className="row g-2 mb-3">
            <div className="col-4"><MetricMini label="Ort. sipariş (AOV)" value={fmtMoney(data.avgOrderValue, sym)} /></div>
            <div className="col-4"><MetricMini label="Toplam gelir" value={fmtMoney(data.totalRevenue, sym)} /></div>
            <div className="col-4"><MetricMini label="Para birimi" value={data.currency || "TRY"} /></div>
          </div>
          <p className="text-muted small mb-0">
            AOV = Toplam gelir ÷ sipariş adedi. Tüm siparişler varsayılan para birimine ({data.currency}) dönüştürülerek hesaplanmıştır.
          </p>
        </>
      );
    }

    if (id === "cargo") {
      return (
        <div className="row g-2">
          <div className="col-4"><MetricMini label="Ort. kargo" value={fmtMoney(data.avgCargo, sym)} /></div>
          <div className="col-4">
            <MetricMini
              label="Sipariş başına kargo payı"
              value={data.avgOrderValue > 0 ? `%${((data.avgCargo / data.avgOrderValue) * 100).toFixed(1)}` : "—"}
            />
          </div>
          <div className="col-4"><MetricMini label="Para birimi" value={data.currency || "TRY"} /></div>
        </div>
      );
    }

    if (id === "daily") {
      return (
        <div className="row g-2">
          <div className="col-6"><MetricMini label="Bugün" value={fmtMoney(data.dailyRevenue, sym)} color="#1D9E75" /></div>
          <div className="col-6">
            <MetricMini
              label="Toplam içindeki pay"
              value={data.totalRevenue > 0 ? `%${((data.dailyRevenue / data.totalRevenue) * 100).toFixed(2)}` : "—"}
            />
          </div>
        </div>
      );
    }

    if (id === "weekly") {
      return (
        <div className="row g-2">
          <div className="col-6"><MetricMini label="Son 7 gün" value={fmtMoney(data.weeklyRevenue, sym)} color="#378ADD" /></div>
          <div className="col-6"><MetricMini label="Günlük ortalama" value={fmtMoney(data.weeklyRevenue / 7, sym)} /></div>
        </div>
      );
    }

    if (id === "monthly") {
      return (
        <>
          <div className="row g-2 mb-3">
            <div className="col-4"><MetricMini label="Son 30 gün" value={fmtMoney(data.monthlyRevenue, sym)} color="#534AB7" /></div>
            <div className="col-4"><MetricMini label="Günlük ort." value={fmtMoney(data.monthlyRevenue / 30, sym)} /></div>
            <div className="col-4">
              <MetricMini
                label="Toplam içindeki pay"
                value={data.totalRevenue > 0 ? `%${((data.monthlyRevenue / data.totalRevenue) * 100).toFixed(1)}` : "—"}
              />
            </div>
          </div>
          <p className="text-muted small mb-2">Periyodik gelir karşılaştırması</p>
          <div style={{ position: "relative", width: "100%", height: 200 }}>
            <canvas ref={chartRefMonthly} />
          </div>
        </>
      );
    }

    if (id === "country") {
      const countries = [...(data.countryStats || [])].sort((a, b) => parseFloat(b.revenue) - parseFloat(a.revenue));
      const maxRev = Math.max(...countries.map((c) => parseFloat(c.revenue || 0)), 1);
      return (
        <>
          <p className="text-muted small mb-3">
            Ülke bazlı sipariş adedi ve dönüştürülmüş gelir ({data.currency})
          </p>
          <table className="table table-hover table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th>Ülke</th>
                <th>Sipariş</th>
                <th>Gelir</th>
                <th style={{ width: 130 }}>Dağılım</th>
              </tr>
            </thead>
            <tbody>
              {countries.map((c, i) => (
                <tr key={i}>
                  <td>
                    <FiMapPin style={{ marginRight: 4, opacity: 0.4, fontSize: 12 }} />
                    {c.country || "Bilinmiyor"}
                  </td>
                  <td>{fmtNum(c.order_count)}</td>
                  <td><strong>{fmtMoney(c.revenue, sym)}</strong></td>
                  <td><Bar value={parseFloat(c.revenue || 0)} max={maxRev} color="#D85A30" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      );
    }

    return null;
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h6 className="text-muted text-uppercase fw-semibold mb-0" style={{ fontSize: 11, letterSpacing: "0.08em" }}>
          Satış & Gelir Analizi
        </h6>
        {loading && <div className="spinner-border spinner-border-sm text-secondary" style={{ width: 14, height: 14 }} />}
      </div>

      <div className="row g-3 mb-2">
        {cards.map((card) => {
          const isOpen = openPanel === card.id;
          return (
            <div key={card.id} className="col-6 col-md-3">
              <div
                className={`card h-100 ${isOpen ? "border-primary" : ""}`}
                style={{ cursor: "pointer", transition: "border-color .15s" }}
                onClick={() => setOpenPanel(isOpen ? null : card.id)}
              >
                <div className="card-body p-3">
                  <div className="d-flex align-items-start justify-content-between mb-2">
                    <div className={`avatar-text avatar-sm ${card.iconClass}`} style={{ fontSize: 14 }}>
                      {card.icon}
                    </div>
                    <FiChevronRight
                      style={{ fontSize: 14, color: "#adb5bd", transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .2s" }}
                    />
                  </div>
                  <div className="text-muted mb-1" style={{ fontSize: 11 }}>{card.label}</div>
                  <div className="fw-semibold" style={{ fontSize: 18, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {card.value}
                  </div>
                  <div className="text-muted mt-1" style={{ fontSize: 11 }}>{card.sub}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {openPanel && (
        <div className="card mb-2">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold">{cards.find((c) => c.id === openPanel)?.label}</h6>
              <button className="btn btn-sm btn-light" onClick={() => setOpenPanel(null)}>
                <FiX style={{ fontSize: 14 }} />
              </button>
            </div>
            {renderPanel(openPanel)}
          </div>
        </div>
      )}
    </div>
  );
}